import * as L from 'leaflet';
import { ReactElement, useEffect, useRef } from 'react';
import { FeatureGroup, LayersControl, Pane, useMap } from 'react-leaflet';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@casops/store/hooks';
import TrackDisplay from '../track-display/TrackDisplay';
import { TRACKS_PANE_NAME, TRACKS_PANE_ZINDEX } from '../../mapConfig';
import { point } from 'leaflet';
import useMapProvider from '../../hooks/useMapProvider';

interface TracksLayerGroupProps {
  checked?: boolean;
}

export const TracksLayerGroup = ({
  checked = true,
}: TracksLayerGroupProps): ReactElement | null => {
  const { t } = useTranslation();

  const { trackIds, viewTracksRequestTimestamp } = useAppSelector(state => state.assetTracks);
  const { maxNativeZoom } = useMapProvider();

  const timestampRef = useRef<number>(viewTracksRequestTimestamp);
  const groupRef = useRef<L.FeatureGroup>(null);
  const map = useMap();

  // Change the map view to fit the visible tracks when a user requests it.
  useEffect(() => {
    // Only execute the effect when the timestamp changes through user action.
    if (groupRef.current && viewTracksRequestTimestamp > timestampRef.current) {
      timestampRef.current = viewTracksRequestTimestamp;
      const bounds = groupRef.current.getBounds();

      if (bounds.isValid()) {
        // Calc the zoom level needed to fit the bounds in the map view with a padding buffer.
        const maxZoom = map.getBoundsZoom(bounds, false, point([40, 40]));
        // Restrict the zoom to not exceed the safe limit.
        const zoomLevel = Math.min(maxNativeZoom, maxZoom);
        // Update the map position and zoom without animation.
        map.setView(bounds.getCenter(), zoomLevel, { animate: false });
      }
    }
  }, [map, maxNativeZoom, viewTracksRequestTimestamp]);

  return trackIds.length > 0 ? (
    <LayersControl.Overlay name={t('Tracks')} checked={checked}>
      <Pane name={TRACKS_PANE_NAME} style={{ zIndex: TRACKS_PANE_ZINDEX }}>
        {/*
          Use one top-level FeatureGroup so that every nested layer is controlled by the one layer control.
          Keep the key static so that the layer control persists the checked state when the inner layers are re-rendered.
        */}
        <FeatureGroup key="keepSingleControlAlive" ref={groupRef}>
          {/* Draw the track that belongs to the left-most card at the top of the pile. */}
          {[...trackIds].reverse().map((trackId, index) => (
            <TrackDisplay key={trackId} trackId={trackId} index={index} />
          ))}
        </FeatureGroup>
      </Pane>
    </LayersControl.Overlay>
  ) : null;
};

export default TracksLayerGroup;
